// ReferralsView (admin) — relatórios de indicação.
// Duas tabs: Consolidado (KPIs + top indicadores) e Por indicador (drill-down
// quando admin clica num user na tab consolidado OU vem por deep-link do
// UsuariosView).

import { useEffect, useState } from "preact/hooks";
import {
  ArrowLeft,
  BarChart3,
  Gift,
  Loader2,
  Sparkles,
  Users
} from "lucide-react";
import {
  api,
  type ReferralSummary,
  type ReferralTopRow,
  type ReferralIndicatedRow
} from "./api";

function formatMoney(centavos: number): string {
  if (!centavos) return "—";
  return `R$ ${(centavos / 100).toFixed(2).replace(".", ",")}`;
}

export function ReferralsView() {
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [top, setTop] = useState<ReferralTopRow[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  // Drill-down (analítico) — quando admin clica num indicador do top.
  const [drillUser, setDrillUser] = useState<ReferralTopRow | null>(null);
  const [drillRows, setDrillRows] = useState<ReferralIndicatedRow[]>([]);
  const [drillPhase, setDrillPhase] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const [drillErr, setDrillErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setPhase("loading");
      try {
        const r = await api.referralsOverview();
        if (cancelled) return;
        setSummary(r.summary);
        setTop(r.topIndicators);
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e instanceof Error ? e.message : String(e));
        setPhase("error");
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function openDrill(row: ReferralTopRow) {
    setDrillUser(row);
    setDrillPhase("loading");
    setDrillErr(null);
    try {
      const r = await api.referralsForUser(row.userId);
      setDrillRows(r.indicated);
      setDrillPhase("ready");
    } catch (e) {
      setDrillErr(e instanceof Error ? e.message : String(e));
      setDrillPhase("error");
    }
  }

  if (phase === "loading") {
    return (
      <div class="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 size-4 animate-spin" />
        Carregando relatórios…
      </div>
    );
  }
  if (phase === "error" || !summary) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Erro: {errMsg ?? "tente recarregar"}.
      </div>
    );
  }

  // Drill-down detail
  if (drillUser) {
    return (
      <div class="space-y-5">
        <header>
          <button
            type="button"
            onClick={() => {
              setDrillUser(null);
              setDrillRows([]);
              setDrillPhase("idle");
            }}
            class="mb-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            <ArrowLeft className="size-3.5" />
            Voltar ao consolidado
          </button>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Sparkles className="size-6 text-violet-600" />
            Indicações de {drillUser.displayName}
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Código <span class="font-mono font-bold">{drillUser.referralCode ?? "—"}</span>{" "}
            · {drillUser.countReferred} indicados · {drillUser.countPaying} pagantes
          </p>
        </header>

        {drillPhase === "loading" && (
          <div class="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Carregando…
          </div>
        )}
        {drillPhase === "error" && (
          <div class="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {drillErr}
          </div>
        )}
        {drillPhase === "ready" && drillRows.length === 0 && (
          <div class="rounded-md border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
            Nenhum indicado encontrado.
          </div>
        )}
        {drillPhase === "ready" && drillRows.length > 0 && (
          <div class="overflow-hidden rounded-md border border-border">
            <table class="w-full text-sm">
              <thead class="bg-muted/40 text-left">
                <tr>
                  <th class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Indicado
                  </th>
                  <th class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Cadastro
                  </th>
                  <th class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th class="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Total pago
                  </th>
                  <th class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Assinatura
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border">
                {drillRows.map((u) => (
                  <tr key={u.id} class="hover:bg-muted/20">
                    <td class="px-3 py-2 font-medium">{u.displayName}</td>
                    <td class="px-3 py-2 text-xs text-muted-foreground tabular-nums">
                      {new Date(u.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td class="px-3 py-2">
                      {u.suspended ? (
                        <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                          Suspenso
                        </span>
                      ) : (
                        <span class="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                          Ativo
                        </span>
                      )}
                    </td>
                    <td class="px-3 py-2 text-right tabular-nums">
                      {formatMoney(u.paidCentavos)}
                    </td>
                    <td class="px-3 py-2">
                      {u.hasActiveSub && (
                        <span class="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">
                          Ativa
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Consolidado
  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Gift className="size-6 text-violet-600" />
          Indicações (referral)
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Quem está trazendo gente nova. Clique num indicador pra ver o
          drill-down com pagamentos.
        </p>
      </header>

      {/* KPIs */}
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total de usuários"
          value={summary.totalUsers}
          icon={Users}
        />
        <KpiCard
          label="Vieram via indicação"
          value={summary.totalReferred}
          icon={Sparkles}
          tone="primary"
        />
        <KpiCard
          label="Indicadores ativos"
          value={summary.totalIndicators}
          icon={Gift}
        />
        <KpiCard
          label="% via indicação"
          value={`${summary.conversionPct}%`}
          icon={BarChart3}
          tone="emerald"
        />
      </div>

      {/* Top indicadores */}
      <div>
        <h2 class="mb-2 text-sm font-bold">Top indicadores</h2>
        {top.length === 0 ? (
          <div class="rounded-md border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
            Ninguém indicou ainda.
          </div>
        ) : (
          <div class="overflow-hidden rounded-md border border-border">
            <table class="w-full text-sm">
              <thead class="bg-muted/40 text-left">
                <tr>
                  <th class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Indicador
                  </th>
                  <th class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Código
                  </th>
                  <th class="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Indicados
                  </th>
                  <th class="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Pagantes
                  </th>
                  <th class="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Taxa
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border">
                {top.map((r) => {
                  const rate =
                    r.countReferred > 0
                      ? Math.round((r.countPaying / r.countReferred) * 100)
                      : 0;
                  return (
                    <tr key={r.userId} class="hover:bg-muted/20">
                      <td class="px-3 py-2 font-medium">
                        {r.displayName}
                        {r.suspended && (
                          <span class="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">
                            suspenso
                          </span>
                        )}
                      </td>
                      <td class="px-3 py-2 font-mono text-xs">
                        {r.referralCode ?? "—"}
                      </td>
                      <td class="px-3 py-2 text-right tabular-nums">
                        {r.countReferred}
                      </td>
                      <td class="px-3 py-2 text-right tabular-nums">
                        {r.countPaying}
                      </td>
                      <td class="px-3 py-2 text-right tabular-nums text-xs text-muted-foreground">
                        {rate}%
                      </td>
                      <td class="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => void openDrill(r)}
                          class="rounded-md border border-border bg-background px-2.5 py-1 text-[11px] font-semibold hover:bg-accent"
                        >
                          Detalhar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tone
}: {
  label: string;
  value: number | string;
  icon: typeof Users;
  tone?: "primary" | "emerald";
}) {
  const toneClasses =
    tone === "primary"
      ? "border-primary/30 bg-primary/5"
      : tone === "emerald"
        ? "border-emerald-500/30 bg-emerald-500/5"
        : "border-border bg-card";
  const labelClasses =
    tone === "primary"
      ? "text-primary"
      : tone === "emerald"
        ? "text-emerald-700 dark:text-emerald-300"
        : "text-muted-foreground";
  return (
    <div class={`rounded-md border p-4 ${toneClasses}`}>
      <div class={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${labelClasses}`}>
        <Icon className="size-3.5" />
        {label}
      </div>
      <div class="mt-1 text-3xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
